/**
 * ResolutionDetector Hook
 *
 * Detects screen resolution and notifies LiveComponent about layout changes.
 * Used for responsive Kiosk Calendar layouts (vertical vs matrix).
 *
 * Usage:
 *   phx-hook="ResolutionDetector"
 *   data-breakpoint="1024"
 *   data-event="resolution_changed"
 */

export const ResolutionDetectorHook = {
  mounted() {
    console.warn('🎯 [ResolutionDetector] HOOK MOUNTED! Element ID:', this.el.id);
    console.log('Element:', this.el);
    console.log('phx-target:', this.el.getAttribute('phx-target'));

    // Check for debug mode via URL parameter
    const params = new URLSearchParams(window.location.search);
    this.debugLayout = params.get('debug_layout');
    if (this.debugLayout) {
      console.warn('🎯 [ResolutionDetector] DEBUG MODE: Forcing layout =', this.debugLayout);
    }

    // Small delay to ensure component is fully mounted
    setTimeout(() => {
      console.warn('🎯 [ResolutionDetector] Calling detectAndNotify after mount delay');
      this.detectAndNotify();
    }, 100);

    // Listen for resize events with debouncing
    this.resizeHandler = this.debounce(() => {
      this.detectAndNotify();
    }, 300);

    window.addEventListener('resize', this.resizeHandler);
    console.log('🎯 [ResolutionDetector] Resize listener attached');
  },

  destroyed() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  },

  detectAndNotify() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Determine layout based on resolution (or debug override)
    let layout = this.debugLayout || this.getLayout(width, height);
    const breakpoint = parseInt(this.el.dataset.breakpoint || '1024');
    const event = this.el.dataset.event || 'resolution_changed';
    const aspectRatio = (width / height).toFixed(2);

    // Debug logging
    const debugNote = this.debugLayout ? ' [DEBUG MODE]' : '';
    console.warn(`🎯 [ResolutionDetector] ${width}×${height}px, Aspect: ${aspectRatio}, Layout: ${layout}${debugNote}, Sending event: ${event}`);

    // Store current layout in element
    this.el.dataset.currentLayout = layout;
    this.el.dataset.screenWidth = width;
    this.el.dataset.screenHeight = height;

    // Notify LiveComponent via custom DOM event
    try {
      console.warn(`🎯 [ResolutionDetector] Dispatching custom event '${event}'`);
      const customEvent = new CustomEvent('resolution-detected', {
        detail: {
          width,
          height,
          layout,
          is_small: width <= breakpoint,
          aspect_ratio: aspectRatio
        },
        bubbles: true
      });
      this.el.dispatchEvent(customEvent);
      console.warn(`🎯 [ResolutionDetector] Custom event dispatched`);

      // ALSO try pushEvent in case it works
      this.pushEvent(event, {
        width,
        height,
        layout,
        is_small: width <= breakpoint,
        aspect_ratio: aspectRatio
      });
      console.warn(`🎯 [ResolutionDetector] pushEvent called`);
    } catch (error) {
      console.error(`🎯 [ResolutionDetector] Error sending event:`, error);
    }
  },

  getLayout(width, height) {
    const aspectRatio = width / height;

    // Small/vertical screens - prioritize this check
    if (width <= 768) {
      return 'compact';
    }

    // 1024x768 or similar (4:3 aspect ratio = 1.33)
    // More flexible: accept any screen width <= 1100 with 4:3 ratio
    if (width <= 1100 && aspectRatio >= 1.28 && aspectRatio <= 1.38) {
      return 'matrix';
    }

    // Default: vertical layout with cards
    return 'vertical';
  },

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
};
